// src/components/question/QuestionContainer.tsx
// ✅ formatStatement: converte \n em <br> para texto puro (questões antigas)
//    e passa HTML do editor rico intacto (questões novas)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    ChevronLeft, ChevronRight, Star,
    Check, X, BookOpen, Video, ChevronDown, ChevronUp,
    TrendingUp, Award, Clock, AlertTriangle, LockKeyhole, ExternalLink, FlaskConical, Pencil
} from 'lucide-react';
import { Question, QuestionStats as Stats, QuestionAlternative } from '../../types/question.types';
import { questionService } from '../../services/questionService';
import { savedQuestionService } from '../../services/savedQuestionService';
import QuestionStats from './QuestionStats';
import { ImageZoomModal } from '../CardEditor/ImageZoomModal';
import ReportErrorModal from './ReportErrorModal';
import './QuestionContainer.css';
import { useAuth } from '../../contexts/AuthContext';
import QuestionLimitModal from './QuestionLimitModal';

interface QuestionContainerProps {
    questionId: string;
    onQuestionChange?: (questionId: string) => void;
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
    allQuestionIds?: string[];
    filtersBar?: React.ReactNode;
    showPageHeader?: boolean;
    freeQuestionIds?: string[];
    hasFullAccess?: boolean;
    onEditQuestion?: (questionId: string) => void;
}

interface CachedQuestion {
    question: Question;
    userProgress: { answered: boolean; selectedAlternative: string | null };
    stats: Stats | null;
}

/* ═══════════════════════════════════════════════════════════════
   formatStatement
   ─ Se o texto já contém tags HTML (editor rico) → exibe como está.
   ─ Se é texto puro com \n (questões antigas) → converte \n em <br>
     e escapa < > & para evitar injeção acidental.
═══════════════════════════════════════════════════════════════ */
function formatStatement(text: string): string {
    if (!text) return '';
    // Detecta se já é HTML: contém pelo menos uma tag completa
    if (/<[a-z][\s\S]*?>/i.test(text)) return text;
    // Texto puro: escapa caracteres especiais e converte quebras
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br />');
}

export const logAnswer = (isCorrect: boolean) => {
    try {
        const stored = localStorage.getItem('graia_answers_log');
        const log: { ts: number; correct: boolean }[] = stored ? JSON.parse(stored) : [];
        log.push({ ts: Date.now(), correct: isCorrect });
        const cutoff24h = Date.now() - 86_400_000;
        const trimmed = log.filter(e => e.ts >= cutoff24h);
        localStorage.setItem('graia_answers_log', JSON.stringify(trimmed));
    } catch { /* silencioso */ }
};

type TimeFilter = '1h' | '6h' | 'today';

const TIME_FILTERS: { id: TimeFilter; label: string }[] = [
    { id: '1h', label: 'Última hora' },
    { id: '6h', label: 'Últimas 6h' },
    { id: 'today', label: 'Hoje' },
];

const getFilteredStats = (rawStats: any, filter: TimeFilter) => {
    if (!rawStats) return null;
    try {
        const stored = localStorage.getItem('graia_answers_log');
        const log: { ts: number; correct: boolean }[] = stored ? JSON.parse(stored) : [];
        const now = Date.now();
        const startOfDay = new Date(); startOfDay.setHours(0, 0, 0, 0);
        const cutoff =
            filter === '1h' ? now - 3_600_000 :
                filter === '6h' ? now - 21_600_000 :
                    startOfDay.getTime();
        const filtered = log.filter(e => e.ts >= cutoff);
        const totalAnswered = filtered.length;
        const totalCorrect = filtered.filter(e => e.correct).length;
        const totalWrong = totalAnswered - totalCorrect;
        const accuracy = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
        return { totalAnswered, totalCorrect, totalWrong, accuracy };
    } catch { return rawStats; }
};

// ─── SAVE BUTTON ─────────────────────────────────────────────
const SaveButton: React.FC<{
    questionId: string;
    onShowToast?: (msg: string, type: 'success' | 'error' | 'info') => void;
}> = ({ questionId, onShowToast }) => {
    const [isSaved, setIsSaved] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        savedQuestionService.checkIfSaved(questionId).then(setIsSaved).catch(() => { });
    }, [questionId]);

    const toggle = async () => {
        try {
            setLoading(true);
            if (isSaved) {
                await savedQuestionService.removeQuestion(questionId);
                setIsSaved(false);
                onShowToast?.('Removido do caderno', 'info');
            } else {
                await savedQuestionService.saveQuestion(questionId);
                setIsSaved(true);
                onShowToast?.('Salvo no caderno', 'success');
            }
        } catch (e: any) {
            onShowToast?.(e.message || 'Erro', 'error');
        } finally { setLoading(false); }
    };

    return (
        <button
            className={`qc-save-btn ${isSaved ? 'saved' : ''}`}
            onClick={toggle} disabled={loading}
            title={isSaved ? 'Remover do caderno' : 'Salvar no caderno'}
        >
            <Star size={18} fill={isSaved ? 'currentColor' : 'none'} />
        </button>
    );
};

// ─── ALTERNATIVES ─────────────────────────────────────────────
const Alternatives: React.FC<{
    alternatives: QuestionAlternative[];
    selected: 'A' | 'B' | 'C' | 'D' | 'E' | null;
    correct: 'A' | 'B' | 'C' | 'D' | 'E' | null;
    showResult: boolean;
    disabled: boolean;
    struckAlts: Set<string>;
    onSelect: (l: 'A' | 'B' | 'C' | 'D' | 'E') => void;
    onToggleStrike: (letter: string) => void;
}> = ({ alternatives, selected, correct, showResult, disabled, struckAlts, onSelect, onToggleStrike }) => {
    const cls = (letter: string) => {
        const base = ['qc-alt'];
        if (disabled) base.push('qc-alt--disabled');
        if (selected === letter) base.push('qc-alt--selected');
        if (showResult && correct) {
            if (letter === correct) base.push('qc-alt--correct');
            else if (letter === selected) base.push('qc-alt--wrong');
        }
        if (struckAlts.has(letter)) base.push('qc-alt--struck');
        return base.join(' ');
    };
    return (
        <div className="qc-alts">
            {alternatives.map(alt => (
                <div key={alt.letter} className="qc-alt-wrap">
                    <button
                        className={cls(alt.letter)}
                        onClick={() => !disabled && onSelect(alt.letter as any)}
                        disabled={disabled}
                    >
                        <span className="qc-alt-letter">{alt.letter}</span>
                        <span className={`qc-alt-text${struckAlts.has(alt.letter) ? ' qc-alt-text--struck' : ''}`}>{alt.text}</span>
                        {showResult && correct && (
                            <span className="qc-alt-icon">
                                {alt.letter === correct && <Check size={16} className="icon-ok" />}
                                {alt.letter === selected && alt.letter !== correct && <X size={16} className="icon-err" />}
                            </span>
                        )}
                    </button>
                    {!showResult && (
                        <button
                            className={`qc-strike-btn${struckAlts.has(alt.letter) ? ' qc-strike-btn--on' : ''}`}
                            onClick={() => onToggleStrike(alt.letter)}
                            title={struckAlts.has(alt.letter) ? 'Remover riscado' : 'Riscar alternativa'}
                            type="button"
                        >
                            <X size={13} />
                        </button>
                    )}
                </div>
            ))}
        </div>
    );
};

// ─── RESOLUTION ───────────────────────────────────────────────
const Resolution: React.FC<{
    explanationText?: string | null;
    explanationVideoUrl?: string | null;
    correctAlternative: 'A' | 'B' | 'C' | 'D' | 'E';
    onZoomImage: (url: string) => void;
}> = ({ explanationText, explanationVideoUrl, correctAlternative, onZoomImage }) => {
    const [showText, setShowText] = useState(true);
    const [showVideo, setShowVideo] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!bodyRef.current) return;
        bodyRef.current.querySelectorAll<HTMLImageElement>('img').forEach(img => {
            img.style.cursor = 'zoom-in';
            img.onclick = () => onZoomImage(img.src);
        });
    }, [explanationText, onZoomImage]);

    const embedUrl = (() => {
        if (!explanationVideoUrl) return null;
        const m = explanationVideoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
        return m ? `https://www.youtube.com/embed/${m[1]}` : null;
    })();

    if (!explanationText && !embedUrl) return null;

    return (
        <div className="qc-resolution">
            <div className="qc-resolution-badge">
                <Check size={16} />
                <span>Resposta correta: <strong>{correctAlternative}</strong></span>
            </div>

            {explanationText && (
                <div className="qc-resolution-section">
                    <button className="qc-resolution-toggle" onClick={() => setShowText(!showText)}>
                        <BookOpen size={16} /><span>Resolução</span>
                        {showText ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {showText && (
                        <div
                            ref={bodyRef}
                            className="qc-resolution-body"
                            dangerouslySetInnerHTML={{ __html: explanationText }}
                        />
                    )}
                </div>
            )}

            {embedUrl && (
                <div className="qc-resolution-section">
                    <button className="qc-resolution-toggle" onClick={() => setShowVideo(!showVideo)}>
                        <Video size={16} /><span>Resolução em Vídeo</span>
                        {showVideo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {showVideo && (
                        <div className="qc-resolution-video">
                            <iframe src={embedUrl} title="Vídeo" allowFullScreen
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function stripHtmlForEdit(html: string): string {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').trim();
}

// ─── ANALYSIS PANEL (revisores / admin) ──────────────────────
const AnalysisPanel: React.FC<{
    questionId: string;
    question: Question;
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}> = ({ questionId, question, onShowToast }) => {
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [difficulty, setDifficulty] = useState('');
    const [suggestedStatement, setSuggestedStatement] = useState(() => stripHtmlForEdit(question.statement));
    const [suggestedAlts, setSuggestedAlts] = useState<{ letter: string; text: string }[]>(
        () => question.alternatives.map(a => ({ letter: a.letter, text: a.text }))
    );
    const [cat1, setCat1] = useState('');
    const [cat2, setCat2] = useState('');
    const [cat3, setCat3] = useState('');
    const [cat4, setCat4] = useState('');
    const [improvementText, setImprovementText] = useState('');
    const [shouldDelete, setShouldDelete] = useState<boolean | null>(null);
    const [shouldRedo, setShouldRedo] = useState<'totalmente' | 'parcialmente' | 'nao' | null>(null);
    const [canPublish, setCanPublish] = useState<boolean | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [canPublishError, setCanPublishError] = useState(false);

    const updateAlt = (letter: string, text: string) => {
        setSuggestedAlts(prev => prev.map(a => a.letter === letter ? { ...a, text } : a));
    };

    const handleSubmit = async () => {
        if (canPublish === null) {
            setCanPublishError(true);
            return;
        }
        setCanPublishError(false);
        setSubmitting(true);
        try {
            const data: any = {};
            if (isCorrect !== null) data.isCorrect = isCorrect;
            if (difficulty) data.suggestedDifficulty = difficulty;
            if (cat1 || cat2 || cat3 || cat4) {
                data.suggestedCategory = { level1: cat1, level2: cat2, level3: cat3, level4: cat4 };
            }
            if (improvementText.trim()) data.improvementText = improvementText.trim();
            if (shouldDelete !== null) data.shouldDelete = shouldDelete;
            if (shouldRedo !== null) data.shouldRedo = shouldRedo;
            if (canPublish !== null) data.canPublish = canPublish;
            // Enunciado sugerido — só envia se foi alterado
            const origStatement = stripHtmlForEdit(question.statement);
            if (suggestedStatement.trim() !== origStatement) {
                data.suggestedStatement = suggestedStatement.trim();
            }
            // Alternativas sugeridas — só envia se alguma foi alterada
            const changedAlts = suggestedAlts.filter(
                sa => sa.text !== (question.alternatives.find(a => a.letter === sa.letter)?.text ?? '')
            );
            if (changedAlts.length > 0) {
                data.suggestedAlternatives = changedAlts;
            }
            await questionService.submitAnalysis(questionId, data);
            setSubmitted(true);
            onShowToast?.('Análise enviada com sucesso', 'success');
        } catch {
            onShowToast?.('Erro ao enviar análise', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="qca-panel">
                <div className="qca-submitted">
                    <Check size={16} />
                    <span>Análise enviada. Obrigado!</span>
                </div>
            </div>
        );
    }

    return (
        <div className="qca-panel">
            <div className="qca-panel-header">
                <FlaskConical size={15} />
                <span className="qca-panel-title">Análise do Revisor</span>
                <span className="qca-panel-hint">* "Pode publicar?" é obrigatório</span>
            </div>
            <div className="qca-form">

                {/* ── Veredicto + Dificuldade ── */}
                <div className="qca-row">
                    <div className="qca-field">
                        <label className="qca-label">Esta questão está correta?</label>
                        <div className="qca-correctness">
                            <button
                                type="button"
                                className={`qca-correct-btn ${isCorrect === true ? 'active--correct' : ''}`}
                                onClick={() => setIsCorrect(isCorrect === true ? null : true)}
                            >
                                <Check size={13} /> Correta
                            </button>
                            <button
                                type="button"
                                className={`qca-correct-btn ${isCorrect === false ? 'active--incorrect' : ''}`}
                                onClick={() => setIsCorrect(isCorrect === false ? null : false)}
                            >
                                <X size={13} /> Incorreta
                            </button>
                        </div>
                    </div>
                    <div className="qca-field">
                        <label className="qca-label">Dificuldade sugerida</label>
                        <select className="qca-select" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
                            <option value="">— sem sugestão —</option>
                            <option value="facil">Fácil</option>
                            <option value="medio">Médio</option>
                            <option value="dificil">Difícil</option>
                        </select>
                    </div>
                </div>

                {/* ── Excluir / Refazer ── */}
                <div className="qca-row">
                    <div className="qca-field">
                        <label className="qca-label">Essa questão deve ser excluída?</label>
                        <div className="qca-correctness">
                            <button
                                type="button"
                                className={`qca-correct-btn ${shouldDelete === true ? 'active--delete' : ''}`}
                                onClick={() => setShouldDelete(shouldDelete === true ? null : true)}
                            >
                                Sim
                            </button>
                            <button
                                type="button"
                                className={`qca-correct-btn ${shouldDelete === false ? 'active--keep' : ''}`}
                                onClick={() => setShouldDelete(shouldDelete === false ? null : false)}
                            >
                                Não
                            </button>
                        </div>
                    </div>
                    <div className="qca-field">
                        <label className="qca-label">Essa questão deve ser refeita?</label>
                        <div className="qca-redo-btns">
                            {(['totalmente', 'parcialmente', 'nao'] as const).map(v => (
                                <button
                                    key={v}
                                    type="button"
                                    className={`qca-correct-btn ${shouldRedo === v ? 'active--redo' : ''}`}
                                    onClick={() => setShouldRedo(shouldRedo === v ? null : v)}
                                >
                                    {v === 'nao' ? 'Não' : v.charAt(0).toUpperCase() + v.slice(1)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Enunciado sugerido ── */}
                <div className="qca-field">
                    <label className="qca-label">Enunciado sugerido <span className="qca-label-hint">(edite caso queira propor correção)</span></label>
                    <textarea
                        className="qca-textarea"
                        value={suggestedStatement}
                        onChange={e => setSuggestedStatement(e.target.value)}
                        rows={4}
                    />
                </div>

                {/* ── Alternativas sugeridas ── */}
                <div className="qca-field">
                    <label className="qca-label">Alternativas sugeridas <span className="qca-label-hint">(edite as que precisar)</span></label>
                    <div className="qca-alts-grid">
                        {suggestedAlts.map(alt => (
                            <div key={alt.letter} className="qca-alt-row">
                                <span className="qca-alt-letter">{alt.letter}</span>
                                <textarea
                                    className="qca-textarea qca-textarea--alt"
                                    value={alt.text}
                                    onChange={e => updateAlt(alt.letter, e.target.value)}
                                    rows={2}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Categoria sugerida ── */}
                <div className="qca-cat-grid">
                    <div className="qca-field">
                        <label className="qca-label">Categoria nível 1</label>
                        <input className="qca-input" value={cat1} onChange={e => setCat1(e.target.value)} placeholder="ex: Clínica Médica" />
                    </div>
                    <div className="qca-field">
                        <label className="qca-label">Categoria nível 2</label>
                        <input className="qca-input" value={cat2} onChange={e => setCat2(e.target.value)} placeholder="ex: Cardiologia" />
                    </div>
                    <div className="qca-field">
                        <label className="qca-label">Categoria nível 3</label>
                        <input className="qca-input" value={cat3} onChange={e => setCat3(e.target.value)} placeholder="ex: Arritmias" />
                    </div>
                    <div className="qca-field">
                        <label className="qca-label">Categoria nível 4</label>
                        <input className="qca-input" value={cat4} onChange={e => setCat4(e.target.value)} placeholder="ex: Diagnóstico" />
                    </div>
                </div>

                {/* ── Observações ── */}
                <div className="qca-field">
                    <label className="qca-label">Observações gerais</label>
                    <textarea
                        className="qca-textarea"
                        value={improvementText}
                        onChange={e => setImprovementText(e.target.value)}
                        placeholder="Descreva melhorias, erros ou qualquer outra observação..."
                        rows={3}
                    />
                </div>

                <div className="qca-submit-row">
                    <div className="qca-can-publish-inline">
                        <span className={`qca-label${canPublishError ? ' qca-label--error' : ''}`}>
                            Pode publicar? <span className="qca-required">*</span>
                        </span>
                        <div className="qca-correctness">
                            <button
                                type="button"
                                className={`qca-correct-btn ${canPublish === true ? 'active--correct' : ''} ${canPublishError && canPublish === null ? 'qca-btn--error' : ''}`}
                                onClick={() => { setCanPublish(canPublish === true ? null : true); setCanPublishError(false); }}
                            >
                                <Check size={13} /> Sim
                            </button>
                            <button
                                type="button"
                                className={`qca-correct-btn ${canPublish === false ? 'active--delete' : ''} ${canPublishError && canPublish === null ? 'qca-btn--error' : ''}`}
                                onClick={() => { setCanPublish(canPublish === false ? null : false); setCanPublishError(false); }}
                            >
                                <X size={13} /> Não
                            </button>
                        </div>
                        {canPublishError && (
                            <span className="qca-error-msg">Selecione Sim ou Não antes de enviar</span>
                        )}
                    </div>
                    <button type="button" className="qca-submit-btn" onClick={handleSubmit} disabled={submitting}>
                        <FlaskConical size={14} />
                        {submitting ? 'Enviando...' : 'Enviar análise'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────
const QuestionContainer: React.FC<QuestionContainerProps> = ({
    questionId,
    onQuestionChange,
    onShowToast,
    allQuestionIds = [],
    filtersBar,
    showPageHeader = true,
    freeQuestionIds = [],
    hasFullAccess = false,
    onEditQuestion,
}) => {
    const [question, setQuestion] = useState<Question | null>(null);
    const [selected, setSelected] = useState<'A' | 'B' | 'C' | 'D' | 'E' | null>(null);
    const [answered, setAnswered] = useState(false);
    const [showResult, setShowResult] = useState(false);
    const [stats, setStats] = useState<Stats | null>(null);
    const [initialLoading, setInitialLoading] = useState(true);
    const [transitioning, setTransitioning] = useState(false);
    const [struckAlts, setStruckAlts] = useState<Set<string>>(new Set());
    const [zoomUrl, setZoomUrl] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState(1);
    const [userStats, setUserStats] = useState<any>(null);
    const [timeFilter, setTimeFilter] = useState<TimeFilter>('today');
    const [showReportModal, setShowReportModal] = useState(false);
    const currentIndex = allQuestionIds.findIndex(id => id === questionId);
    const displayIndex = currentIndex >= 0 ? currentIndex + 1 : 1;
    const cache = useRef<Map<string, CachedQuestion>>(new Map());

    const { isAdmin, isRevisor, isVip } = useAuth();
    const [showQuestionLimitModal, setShowQuestionLimitModal] = useState(false);
    const [limitReason, setLimitReason] = useState<'daily_exhausted' | 'total_exhausted' | 'not_free_question' | null>(null);

    const loadUserStats = useCallback(async () => {
        try { setUserStats(await questionService.getUserStats()); } catch { /* silencioso */ }
    }, []);

    useEffect(() => { loadUserStats(); }, [loadUserStats]);

    useEffect(() => { setStruckAlts(new Set()); }, [questionId]);

    const displayStats = getFilteredStats(userStats, timeFilter);

    const prefetch = useCallback(async (id: string) => {
        if (cache.current.has(id)) return;
        try {
            const data = await questionService.getQuestion(id);
            cache.current.set(id, {
                question: data.question,
                userProgress: data.userProgress || { answered: false, selectedAlternative: null },
                stats: data.stats || null,
            });
        } catch { /* silencioso */ }
    }, []);

    useEffect(() => {
        if (!allQuestionIds.length || currentIndex < 0) return;
        const next = allQuestionIds[currentIndex + 1];
        const prev = allQuestionIds[currentIndex - 1];
        if (next) prefetch(next);
        if (prev) prefetch(prev);
    }, [allQuestionIds, currentIndex, prefetch]);

    const applyQuestion = (data: CachedQuestion) => {
        setQuestion(data.question);
        setAnswered(data.userProgress.answered);
        setSelected((data.userProgress.selectedAlternative as any) || null);
        setShowResult(data.userProgress.answered);
        setStats(data.stats);
    };

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            const cached = cache.current.get(questionId);
            if (cached) {
                setTransitioning(true);
                setTimeout(() => {
                    if (!cancelled) { applyQuestion(cached); setTransitioning(false); setInitialLoading(false); }
                }, 80);
            } else {
                if (question === null) setInitialLoading(true); else setTransitioning(true);
                try {
                    const data = await questionService.getQuestion(questionId);
                    const entry: CachedQuestion = {
                        question: data.question,
                        userProgress: data.userProgress || { answered: false, selectedAlternative: null },
                        stats: data.stats || null,
                    };
                    cache.current.set(questionId, entry);
                    if (!cancelled) applyQuestion(entry);
                } catch {
                    onShowToast?.('Erro ao carregar questão', 'error');
                } finally {
                    if (!cancelled) { setInitialLoading(false); setTransitioning(false); }
                }
            }
            if (!cancelled && allQuestionIds.length > 0) {
                const idx = allQuestionIds.findIndex(id => id === questionId);
                if (idx >= 0) {
                    if (allQuestionIds[idx + 1]) prefetch(allQuestionIds[idx + 1]);
                    if (allQuestionIds[idx - 1]) prefetch(allQuestionIds[idx - 1]);
                }
            }
        };
        load();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionId]);

    const handleSelect = (letter: 'A' | 'B' | 'C' | 'D' | 'E') => { if (!answered) setSelected(letter); };

    const handleToggleStrike = (letter: string) => {
        setStruckAlts(prev => {
            const next = new Set(prev);
            if (next.has(letter)) next.delete(letter); else next.add(letter);
            return next;
        });
    };

    const handleSubmit = async () => {
        if (!selected || !question) return;

        // Verificar limite apenas para usuários comuns sem acesso total
        if (!isAdmin && !isRevisor && !isVip && !hasFullAccess) {
            try {
                const token = localStorage.getItem('accessToken');
                const checkRes = await fetch('/api/admin/free-config/question-check', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ questionId })
                });
                console.log('📡 question-check status:', checkRes.status);
                const checkData = await checkRes.json();
                console.log('📡 question-check resposta:', checkData);

                if (!checkData.allowed) {
                    setLimitReason(checkData.reason);
                    setShowQuestionLimitModal(true);
                    return;
                }
            } catch (err) {
                console.error('❌ question-check erro:', err);
                // Se falhar a verificação, deixa passar (fail open)
            }
        }

        try {
            const result = await questionService.answerQuestion(questionId, selected);
            setAnswered(true); setShowResult(true); setStats(result.stats);
            try { logAnswer(result.isCorrect); } catch { }
            loadUserStats();

            // Registrar consumo apenas para usuários comuns sem acesso total
            if (!isAdmin && !isRevisor && !isVip && !hasFullAccess) {
                const token = localStorage.getItem('accessToken');
                fetch('/api/admin/free-config/question-record', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ questionId })
                }).catch(() => { });
            }

            const cached = cache.current.get(questionId);
            if (cached) { cached.userProgress = { answered: true, selectedAlternative: selected }; cached.stats = result.stats; }
            onShowToast?.(result.isCorrect ? 'Resposta correta! ✓' : 'Resposta incorreta', result.isCorrect ? 'success' : 'error');
        } catch { onShowToast?.('Erro ao enviar resposta', 'error'); }
    };

    const navigate = (dir: 'prev' | 'next') => {
        if (!allQuestionIds.length || currentIndex < 0) return;
        const target = dir === 'next' ? allQuestionIds[currentIndex + 1] : allQuestionIds[currentIndex - 1];
        if (target) onQuestionChange?.(target);
        else onShowToast?.(dir === 'next' ? 'Última questão' : 'Primeira questão', 'info');
    };

    const categoryPath = question
        ? [question.category?.level1, question.category?.level2, question.category?.level3, question.category?.level4]
            .filter(v => v && v.trim() !== '').join(' › ')
        : '';

    const diffLabel: Record<string, string> = { facil: 'Fácil', medio: 'Médio', dificil: 'Difícil' };

    if (initialLoading || !question) {
        return (
            <div className="qc-loading">
                <div className="qc-spinner" />
                <p>Carregando questão...</p>
            </div>
        );
    }

    const isBlocked = !isAdmin && !isRevisor && !isVip && !hasFullAccess && !freeQuestionIds.includes(questionId);

    return (
        <>
            {showPageHeader && (
                <>
                    <div className="qc-page-title"><h1>Responder Questões</h1></div>

                    {userStats && (
                        <div className="qstpag-stats-block">
                            <div className="qstpag-time-filters">
                                {TIME_FILTERS.map(f => (
                                    <button
                                        key={f.id}
                                        className={`qstpag-tf-btn ${timeFilter === f.id ? 'qstpag-tf-btn--on' : ''}`}
                                        onClick={() => setTimeFilter(f.id)}
                                    >
                                        {f.id === '1h' && <Clock size={11} />}
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                            <div className="qstpag-stats-grid">
                                <div className="qstpag-stat-item">
                                    <TrendingUp size={16} className="qstpag-stat-icon" />
                                    <div className="qstpag-stat-body">
                                        <span className="qstpag-stat-val">{displayStats?.totalAnswered ?? 0}</span>
                                        <span className="qstpag-stat-lbl">Respondidas</span>
                                    </div>
                                </div>
                                <div className="qstpag-stat-item">
                                    <Award size={16} className="qstpag-stat-icon" />
                                    <div className="qstpag-stat-body">
                                        <span className="qstpag-stat-val">{displayStats?.accuracy ?? 0}%</span>
                                        <span className="qstpag-stat-lbl">Precisão</span>
                                    </div>
                                </div>
                                <div className="qstpag-stat-item">
                                    <div className="qstpag-stat-body">
                                        <span className="qstpag-stat-val qstpag-stat-val--ok">{displayStats?.totalCorrect ?? 0}</span>
                                        <span className="qstpag-stat-lbl">Acertos</span>
                                    </div>
                                </div>
                                <div className="qstpag-stat-item">
                                    <div className="qstpag-stat-body">
                                        <span className="qstpag-stat-val qstpag-stat-val--err">{displayStats?.totalWrong ?? 0}</span>
                                        <span className="qstpag-stat-lbl">Erros</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {filtersBar}

            <div className={`qc-root ${transitioning ? 'qc-fade' : ''}`}>
                <ImageZoomModal
                    imageUrl={zoomUrl}
                    onClose={() => { setZoomUrl(null); setZoomScale(1); }}
                    zoomScale={zoomScale}
                    onZoomChange={setZoomScale}
                />

                <div className={`qc-card ${isBlocked ? 'qc-card--blocked' : ''}`}>
                    {isBlocked && (
                        <div className="qc-blocked-overlay">
                            <div className="qc-blocked-icon">
                                <LockKeyhole size={32} strokeWidth={1.5} />
                            </div>
                            <p>Esta questão requer o plano completo</p>
                            <button
                                className="qc-blocked-btn"
                                onClick={() => window.open('/marketplace', '_blank', 'noopener,noreferrer')}
                            >
                                <ExternalLink size={14} />
                                Ver planos
                            </button>
                        </div>
                    )}
                    <div className="qc-header">
                        {categoryPath && <span className="qc-category">{categoryPath}</span>}
                        <div className="qc-nav-inline">
                            <button className="qc-nav-btn" onClick={() => navigate('prev')} disabled={currentIndex <= 0} title="Anterior">
                                <ChevronLeft size={18} />
                            </button>
                            <span className="qc-counter">
                                <strong>{displayIndex}</strong>
                                <span className="qc-counter-sep">/</span>
                                <span>{allQuestionIds.length || '?'}</span>
                            </span>
                            <button className="qc-nav-btn" onClick={() => navigate('next')} disabled={currentIndex >= allQuestionIds.length - 1} title="Próxima">
                                <ChevronRight size={18} />
                            </button>
                        </div>
                        <div className="qc-header-right">
                            <SaveButton questionId={questionId} onShowToast={onShowToast} />
                            {isAdmin && onEditQuestion && (
                                <button
                                    className="qc-edit-btn"
                                    onClick={() => onEditQuestion(questionId)}
                                    title="Editar esta questão"
                                >
                                    <Pencil size={15} />
                                </button>
                            )}
                            {(isAdmin || isRevisor) && question.status === 'analise' && (
                                <span className="qca-badge-analise">Em análise</span>
                            )}
                            <span className={`qc-diff qc-diff--${question.difficulty}`}>
                                {diffLabel[question.difficulty] || 'Médio'}
                            </span>
                        </div>
                    </div>

                    {/* ✅ formatStatement — preserva HTML rico E converte texto puro com \n */}
                    <div className="qc-statement">
                        <div dangerouslySetInnerHTML={{ __html: formatStatement(question.statement) }} />
                        {question.statementImage && (
                            <div className="qc-statement-img">
                                <img
                                    src={question.statementImage}
                                    alt="Imagem da questão"
                                    onClick={() => { setZoomUrl(question.statementImage!); setZoomScale(1); }}
                                    title="Clique para ampliar"
                                />
                            </div>
                        )}
                    </div>

                    <Alternatives
                        alternatives={question.alternatives}
                        selected={selected}
                        correct={showResult ? question.correctAlternative : null}
                        showResult={showResult}
                        disabled={answered}
                        struckAlts={struckAlts}
                        onSelect={handleSelect}
                        onToggleStrike={handleToggleStrike}
                    />

                    <div className="qc-submit-row">
                        <button
                            className="qc-report-btn"
                            onClick={() => setShowReportModal(true)}
                            title="Reportar erro nesta questão"
                        >
                            <AlertTriangle size={14} />
                            Reportar erro
                        </button>
                        {!answered && (
                            <button
                                className="qc-submit-btn"
                                onClick={handleSubmit}
                                disabled={!selected || isBlocked}
                            >
                                {isBlocked ? <LockKeyhole size={14} /> : null}
                                {isBlocked ? 'Bloqueado' : 'Responder'}
                            </button>
                        )}
                    </div>
                </div>

                {showReportModal && (
                    <ReportErrorModal
                        questionId={questionId}
                        onClose={() => setShowReportModal(false)}
                        onShowToast={onShowToast || (() => { })}
                    />
                )}

                {showResult && (
                    <QuestionStats
                        stats={stats}
                        correctAlternative={question.correctAlternative}
                        userSelectedAlternative={selected}
                    />
                )}

                {showResult && (
                    <Resolution
                        explanationText={question.explanationText}
                        explanationVideoUrl={question.explanationVideoUrl}
                        correctAlternative={question.correctAlternative}
                        onZoomImage={(url) => { setZoomUrl(url); setZoomScale(1); }}
                    />
                )}

                {answered && (isAdmin || isRevisor) && question.status === 'analise' && (
                    <AnalysisPanel questionId={questionId} question={question} onShowToast={onShowToast} />
                )}

                <QuestionLimitModal
                    isOpen={showQuestionLimitModal}
                    reason={limitReason}
                    onClose={() => setShowQuestionLimitModal(false)}
                />
            </div>
        </>
    );
};

export default QuestionContainer;